<template>
	<div class="ir-finance">
		<MStateBox :loading="wrapperLoading" :empty="showList.length === 0">
			<div class="finance-wrapper" v-for="(wrapper, idx) in showList" :key="idx">
				<div class="header">
					<p class="title">
						{{ wrapper.name }}
					</p>
					<m-dropdown
						v-if="showPeriodDropdown(wrapper)"
						:value.sync="wrapper.value"
						:options="getComparisonPeriod"
						position="right"
						:key="wrapper.value"
						@change="value => getData(wrapper, idx)"
					></m-dropdown>
				</div>
				<div class="main">
					<div v-if="!Y_AXIS_RATE_MAP.includes(wrapper.active)" class="unit">百万人民币</div>
					<FinanceModuleTabs :wrapper="wrapper" @tab-change="tabId => changeTab(idx, tabId)" />
					<FinanceModuleChart :wrapper="wrapper" />
				</div>
			</div>
			<m-visible-tips />
		</MStateBox>
	</div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import MStateBox from '@rebuild/components/MStateBox.vue';
import FinanceModuleTabs from '@rebuild/materials/shared/finance_data/FinanceModuleTabs.vue';
import FinanceModuleChart from '@rebuild/materials/shared/finance_data/FinanceModuleChart.vue';
import { useFinanceChartOption } from '@rebuild/materials/shared/finance_data/useFinanceChartOption';
import { priceFormat } from '@/utils/api';
import {
	incomeData,
	grossProfitData,
	grossMarginData,
	coreOperatingProfitData,
	coreOperatingProfitRateData,
	ShareholdersNetProfitData,
	netMarginData,
	netCashData,
	cashDividendsData,
	dividendPayoutRatioData,
	roaData,
	roeData
} from './assest';

const wrapperLoading = ref(true);
const { getThemeColors } = useFinanceChartOption();

const getComparisonPeriod = ref([
	{ text: '年度', value: 'A' },
	{ text: '半年度', value: 'H1' }
]);

// 仅有年度数据的图表不显示周期下拉（按当前选中的子 tab 判断）
const PERIOD_ANNUAL_ONLY_IDS = ['cashDividends', 'dividendPayoutRatio'];
function showPeriodDropdown(wrapper) {
	return !PERIOD_ANNUAL_ONLY_IDS.includes(wrapper?.active);
}

// 每组一个 tabs：id/name 用于 tab 展示，hasYoY/yAxisName 用于图表配置（可省略，默认 hasYoY: false）
const CHART_CONFIG = [
	{
		name: '盈利能力',
		id: 'profitability',
		active: 'incomeData',
		tabs: [
			{ id: 'incomeData', name: '收入', hasYoY: true, yAxisName: '百万人民币' },
			{ id: 'grossProfit', name: '毛利', hasYoY: true, yAxisName: '百万人民币' },
			{ id: 'grossMargin', name: '毛利率' },
			{ id: 'coreOperatingProfit', name: '核心经营利润', hasYoY: true, yAxisName: '百万人民币' },
			{ id: 'coreOperatingProfitRate', name: '核心经营利润率' },
			{ id: 'ShareholdersNetProfit', name: '归属股东净利润', hasYoY: true, yAxisName: '百万人民币' },
			{ id: 'netMargin', name: '净利润率' },
			{ id: 'roe', name: '净资产收益率' }
		]
	},
	{
		name: '偿债能力',
		id: 'debt-paying-ability',
		active: 'netCash',
		tabs: [{ id: 'netCash', name: '净(债务)现金', yAxisName: '百万人民币' }]
	},
	{
		name: '股东回报',
		id: 'shareholder-return',
		active: 'cashDividends',
		tabs: [
			{ id: 'cashDividends', name: '现金股息', yAxisName: '百万人民币' },
			{ id: 'dividendPayoutRatio', name: '股息支付率' },
			{ id: 'roa', name: '资产回报率' }
		]
	}
];

// 每个分组一个周期（A/H1/H2）
const activeValue = ref(CHART_CONFIG.map(() => 'A'));
// 每个分组当前选中的子 tab id（切换 tab 时更新）
const activeSubTabByGroupId = ref(Object.fromEntries(CHART_CONFIG.map(c => [c.id, c.active])));
function changeTab(wrapperIdx, tabId) {
	const cfg = CHART_CONFIG[wrapperIdx];
	if (!cfg) return;
	activeSubTabByGroupId.value = {
		...activeSubTabByGroupId.value,
		[cfg.id]: tabId
	};
}

// id → 数据源（用于按当前子 tab 取对应 seriesData）
const DATA_SOURCE_MAP = {
	incomeData,
	grossProfit: grossProfitData,
	grossMargin: grossMarginData,
	coreOperatingProfit: coreOperatingProfitData,
	coreOperatingProfitRate: coreOperatingProfitRateData,
	ShareholdersNetProfit: ShareholdersNetProfitData,
	netMargin: netMarginData,
	netCash: netCashData,
	cashDividends: cashDividendsData,
	dividendPayoutRatio: dividendPayoutRatioData,
	roa: roaData,
	roe: roeData
};

const defaultChartOptions = { legend: { show: false } };
const customFormatter1 = value => value;
const customFormatter2 = value => (value == 0 ? value : value.toFixed(0) + '%');

const Y_AXIS_RATE_MAP = ['grossMargin', 'coreOperatingProfitRate', 'netMargin', 'dividendPayoutRatio', 'roa', 'roe'];

const showList = computed(() =>
	CHART_CONFIG.map((cfg, idx) => {
		// 当前选中的子 tab id（用于取对应数据与图例）
		const activeId = activeSubTabByGroupId.value[cfg.id] ?? cfg.active;
		const value = PERIOD_ANNUAL_ONLY_IDS.includes(activeId) ? 'A' : activeValue.value[idx];
		const dataSource = DATA_SOURCE_MAP[activeId];
		const seriesData = dataSource ? dataSource[value] : [];
		const dataConfig = cfg.tabs.find(d => d.id === activeId) || cfg.tabs[0];
		const legend = [
			{ color: getThemeColors.value[0], name: dataConfig?.name ?? cfg.name },
			...(dataConfig?.hasYoY ? [{ color: getThemeColors.value[1], name: '同比' }] : [])
		];
		const chartOptions = { ...defaultChartOptions };
		return {
			name: cfg.name,
			value,
			tabList: cfg.tabs,
			active: activeId,
			id: cfg.id,
			chartData: {
				seriesData,
				chartOptions,
				customFormatter: Y_AXIS_RATE_MAP.includes(activeId) ? customFormatter2 : customFormatter1,
				tooltipValueFormatter: val => {
					if (val == '-') return '- -';
					return priceFormat(parseFloat(val).toFixed(1));
				},
				toolTipFixedNum: 1
			},
			legend,
			loading: false,
			isEmpty: false,
			data: []
		};
	})
);

async function initShowList() {
	wrapperLoading.value = false;
}
onMounted(() => {
	initShowList();
});

function getData(module, idx) {
	activeValue.value = activeValue.value.map((v, i) => (i === idx ? module.value : v));
}
</script>

<style lang="less" scoped>
.ir-finance {
	width: 100%;
	padding-top: 0.14rem;
	overflow: hidden;

	.finance-wrapper {
		margin-left: 0.14rem;
		margin-right: 0.14rem;
		padding: 0.14rem 0.12rem;
		border-radius: 0.04rem;
		background-color: #fff;

		& + .finance-wrapper {
			margin-top: 0.14rem;
		}

		.header {
			display: flex;
			align-items: center;
			justify-content: space-between;

			.title {
				color: #333;
				font-family: 'PingFang SC';
				font-size: 0.16rem;
				font-style: normal;
				font-weight: 500;
				line-height: 0.22rem;
			}
			.unit {
				font-size: 0.12rem;
			}
		}

		.main {
			position: relative;
			margin-top: 0.16rem;
			.unit {
				position: absolute;
				left: 0;
				bottom: 2.1rem;
				z-index: 100;
				font-size: 0.11rem;
				color: #989898;
			}
		}
	}
}
</style>
